// Core packages
import {
  CdkDragDrop,
  moveItemInArray
} from "@angular/cdk/drag-drop";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Script start
 */
@Component({
  selector: "app-document-setting-detail-dialog",
  templateUrl: "./document-setting-detail-dialog.component.html",
  styleUrls: ["./document-setting-detail-dialog.component.scss"],
})
export class DocumentSettingDetailDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = true; // Used to view/hide form loading spinner
  title = "";

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  subForm: FormGroup = new FormGroup({});
  newSubForm: FormGroup = new FormGroup({});
  model: any = {};
  subModel: any = {};
  newSubModel: any = {};
  fields: FormlyFieldConfig[] = [];
  subFields: FormlyFieldConfig[] = [];
  newSubFields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  subOptions: FormlyFormOptions = {};
  newSubOptions: FormlyFormOptions = {};

  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  printModuleBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printModule"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-print",
      inline: true,
    },
  };
  printQrBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printLabel"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "accent",
    spinnerColor: "accent",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-qrcode",
      inline: true,
    },
  };
  // END - Edit item form

  newSubformIsVisible = false;
  subformIsVisible = false;
  documents: any[] = [];
  subElementTitle = "";
  structureOptions: any[] = [];

  // BEGIN - Move document form (cross-content move of a 3rd level document)
  allAreas: any[] = [];
  // Destination contents grouped by area; the source content is excluded
  destContentGroups: any[] = [];
  moveDocForm: FormGroup = new FormGroup({
    docId: new FormControl("", Validators.required),
    dest: new FormControl("", Validators.required),
  });
  // END - Move document form

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<DocumentSettingDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private settingsService: SettingsService
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // // Get logged user data
    // this.subscriptions.push(
    //   this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    // );
    // this.subscriptions.push(
    //   this.authService.loggedUserPermissions$.subscribe(
    //     (res) => (this.loggedUserPermissions = res)
    //   )
    // );
    if (
      this.data &&
      this.data.item &&
      this.data.item.translation &&
      this.data.item.translation.it
    ) {
      this.title = this.data.item.translation.it;
      this.documents = Array.isArray(this.data.item.documents)
        ? this.data.item.documents
        : [];
    }

    if (
      this.data.condoStructureTemplate &&
      Array.isArray(this.data.condoStructureTemplate)
    ) {
      for (const structure of this.data.condoStructureTemplate) {
        if (
          !structure.used || 
          (
            this.data.item.elementType && 
            this.data.item.elementType == 'array' &&
            this.data.item.structure === structure.key
            )
        ) {
          this.structureOptions.push({
            value: structure.key,
            label: structure.translation.it,
          });
        }
      }
    }

    this.allAreas = Array.isArray(this.data.areas) ? this.data.areas : [];
    this.buildDestContentGroups();

    this.initForm();
  }

  /**
   * Build the grouped (by area) list of destination contents for the move form.
   * The source content (this.data.subAreaId) is excluded and empty groups are dropped.
   *
   * @since 3.0.0
   */
  buildDestContentGroups(): void {
    this.destContentGroups = [];
    const sourceContentId =
      this.data && this.data.subAreaId ? this.data.subAreaId.toString() : "";

    for (const area of this.allAreas) {
      if (!area || !Array.isArray(area.content)) {
        continue;
      }
      const contents = [];
      for (const content of area.content) {
        if (
          content &&
          content._id &&
          content._id.toString() !== sourceContentId
        ) {
          contents.push({
            value: area._id + "::" + content._id,
            label:
              content.translation && content.translation.it
                ? content.translation.it
                : "",
          });
        }
      }
      if (contents.length) {
        this.destContentGroups.push({
          label:
            area.translation && area.translation.it ? area.translation.it : "",
          contents,
        });
      }
    }
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    this.fields = [];
    this.model = this.data.item;

    if (this.data && this.data.item) {
      this.fields = [
        {
          fieldGroupClassName: "row",
          fieldGroup: [
            // {
            //   key: "elementType",
            //   defaultValue: "object",
            //   templateOptions: {
            //     type: "hidden",
            //   },
            // },
            // {
            //   key: "structure",
            //   defaultValue: "",
            //   templateOptions: {
            //     type: "hidden",
            //   },
            // },
            
            {
              className: "col-12 col-md-5 col-xl-5",
              type: "input",
              key: "translation.it",
              defaultValue: "",
              templateOptions: {
                label: this.translateService.instant("settings.document.it"),
                placeholder: this.translateService.instant(
                  "settings.document.it"
                ),
                required: true,
                minLength: 2,
                maxLength: 100,
              },
            },
            {
              className: "col-12 col-md-5 col-xl-5",
              type: "input",
              key: "translation.en",
              defaultValue: "",
              templateOptions: {
                label: this.translateService.instant("settings.document.en"),
                placeholder: this.translateService.instant(
                  "settings.document.en"
                ),
                required: true,
                minLength: 2,
                maxLength: 100,
              },
            },
            {
              className: "col-12 col-md-2 col-xl-2",
              type: "select",
              key: "deleted",
              templateOptions: {
                label: this.translateService.instant(
                  "settings.document.deleted"
                ),
                placeholder: this.translateService.instant(
                  "settings.document.deleted"
                ),
                required: true,
                multiple: false,
                default: false,
                options: [
                  {
                    value: false,
                    label: "No",
                  },
                  {
                    value: true,
                    label: "Sì",
                  },
                ],
              },
            },
            {
              className: "col-12 col-md-12 col-xl-12",
              type: "select",
              key: "elementType",
              templateOptions: {
                label: this.translateService.instant("settings.document.structureActivation.question"),
                placeholder: this.translateService.instant("settings.document.structureActivation.question"),
                required: true,
                multiple: false,
                default: "object",
                options: [
                  {
                    value: "object",
                    label: this.translateService.instant("settings.document.structureActivation.no"),
                  },
                  {
                    value: "array",
                    label: this.translateService.instant("settings.document.structureActivation.yes"),
                  },
                ],
              },
            },
            {
              className: "col-12 col-md-12 col-xl-12",
              type: "select",
              key: "structure",
              hideExpression: (model): boolean =>
                this.model.elementType === 'object',
              templateOptions: {
                label: this.translateService.instant("settings.document.group"),
                placeholder: this.translateService.instant("settings.document.group"),
                required: true,
                multiple: false,
                default: "object",
                options: this.structureOptions,
              },
            },
          ],
        },
      ];
    }

    this.dialogIsLoading = false;
  }

  /**
   * Handle dialog Submit
   *
   * @since 1.0.0
   */
  onFormSubmit(data?: any): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }
    // Activate submit button loading status
    this.formBtnOptions.active = true;

    if (this.form.value) {
      const dataToSend = {
        subContent: {
          deleted: this.form.value.deleted,
          translation: this.form.value.translation,
          elementType: this.form.value.elementType,
          structure: this.form.value.structure,
        },
        // documents: this.documents.map((el: any) => {
        //   return {
        //     _id: el._id,
        //     translation: el.translation,
        //     deleted: el.deleted,
        //   };
        // }),

        documents: this.documents.map((el: any) => {
          return {
            _id: el._id,
            translation: el.translation,
            deleted: el.deleted,
            mandatory: el.mandatory,
            contract: el.contract,
            key: el.key,
            schemaType: el.schemaType,
            expirationNotice:
              el.expirationNotice === null ? "" : el.expirationNotice,
          };
        }),
      };

      this.subscriptions.push(
        this.settingsService
          .updateDocument(
            this.data.documentTemplateId,
            this.data.areaId,
            this.data.subAreaId,
            dataToSend
          )
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            (res) => {
              if (res.status) {
                this.toastrService.success(res.message);
                // this.dialogRef.close({
                //   save: "success"
                // });
              }
            },
            (err) => {
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }
  }

  /**
   * Handle dialog denial
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    this.dialogRef.close();
  }

  /**
   * Handle drag&drop
   *
   * @since 1.0.0
   */
  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.documents, event.previousIndex, event.currentIndex);
  }

  /**
   * Handle save document template third level
   *
   * @since 1.0.0
   */
  onSubTitleClicked(item: any): void {
    this.subElementTitle = item.translation.it;

    this.newSubformIsVisible = false;

    this.subModel = item;
    this.subFields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            key: "_id",
            defaultValue: "",
            templateOptions: {
              type: "hidden",
            },
          },
          {
            key: "key",
            defaultValue: "",
            templateOptions: {
              type: "hidden",
            },
          },
          {
            key: "deleted",
            defaultValue: "",
            templateOptions: {
              type: "hidden",
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "input",
            key: "translation.it",
            templateOptions: {
              label: this.translateService.instant("settings.document.it"),
              placeholder: this.translateService.instant(
                "settings.document.it"
              ),
              required: true,
              minLength: 2,
              maxLength: 500,
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "input",
            key: "translation.en",
            templateOptions: {
              label: this.translateService.instant("settings.document.en"),
              placeholder: this.translateService.instant(
                "settings.document.en"
              ),
              required: true,
              minLength: 2,
              maxLength: 500,
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-2",
            type: "select",
            key: "schemaType",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.schemaType"
              ),
              placeholder: this.translateService.instant(
                "settings.document.schemaType"
              ),
              required: true,
              multiple: false,
              options: [
                { value: "ASchema", label: "A" },
                { value: "A1Schema", label: "A1" },
                { value: "A3mSchema", label: "A3m" },
                { value: "A4PlusSchema", label: "A4+" },
                { value: "A4Schema", label: "A4" },
                { value: "A5Schema", label: "A5" },
                { value: "A5mSchema", label: "A5m" },
                { value: "A7Schema", label: "A7" },
                { value: "A8Schema", label: "A8" },
                { value: "A8PlusSchema", label: "A8+" },
                { value: "A9Schema", label: "A9" },
                { value: "B2Schema", label: "B2" },
                { value: "B3Schema", label: "B3" },
                { value: "B5Schema", label: "B5" },
                { value: "B6Schema", label: "B6" },
                { value: "B6StarSchema", label: "B6*" },
                { value: "BBMinusSchema", label: "BB-" },
                { value: "BBPlusSchema", label: "BB+" },
                { value: "BBSchema", label: "BB" },
                { value: "BBSlashSchema", label: "BB/" },
                { value: "BBStarSchema", label: "BB*" },
                { value: "BMinusSchema", label: "B-" },
                { value: "BPlusSchema", label: "B+" },
                { value: "BSchema", label: "B" },
                { value: "BSlashSchema", label: "B/" },
                { value: "BStarSchema", label: "B*" },
                { value: "BXSchema", label: "BX" },
                { value: "C1Schema", label: "C1" },
                { value: "D1Schema", label: "D1" },
                { value: "DSchema", label: "D" },
                { value: "E1Schema", label: "E1" },
                { value: "ESchema", label: "E" },
                { value: "F1Schema", label: "F1" },
                { value: "F2Schema", label: "F2" },
                { value: "FSchema", label: "F" },
                { value: "G1Schema", label: "G1" },
                { value: "GMinusSchema", label: "G-" },
                { value: "GPlusSchema", label: "G+" },
                { value: "GSchema", label: "G" },
                { value: "GSlashSchema", label: "G/" },
                { value: "GStarSchema", label: "G*" },
                { value: "H1Schema", label: "H1" },
                { value: "I1Schema", label: "I1" },
                { value: "ISchema", label: "I" },
                { value: "L2Schema", label: "L2" },
                { value: "LPlusSchema", label: "L+" },
                { value: "LSchema", label: "L" },
                { value: "M1Schema", label: "M1" },
                { value: "NSchema", label: "N" },
                { value: "PSchema", label: "P" },
                { value: "QSchema", label: "Q" },
                { value: "RSchema", label: "R" },
                { value: "SSchema", label: "S" },
                { value: "TSchema", label: "T" },
                { value: "TXSchema", label: "TX" },
                { value: "USchema", label: "U" },
                { value: "VSchema", label: "V" },
                { value: "WPlusSchema", label: "W+" },
                { value: "WSchema", label: "W" },
                { value: "XSchema", label: "X" },
                { value: "XX1MinusSchema", label: "XX1-" },
                { value: "XX1PlusSchema", label: "XX1+" },
                { value: "XX1Schema", label: "XX1" },
                { value: "XX1SlashSchema", label: "XX1/" },
                { value: "XX1StarSchema", label: "XX1*" },
                { value: "YSchema", label: "Y" },
                { value: "Z1Schema", label: "Z1" },
                { value: "Z1PlusSchema", label: "Z1+" },
                { value: "Z3Schema", label: "Z3" },
                { value: "Z4Schema", label: "Z4" },
                { value: "Zschema", label: "Zschema" },
              ],
            },
          },
          {
            className: "col-2",
            type: "select",
            key: "mandatory",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.mandatory"
              ),
              placeholder: this.translateService.instant(
                "settings.document.mandatory"
              ),
              required: true,
              multiple: false,
              options: [
                {
                  value: false,
                  label: "No",
                },
                {
                  value: true,
                  label: "Sì",
                },
              ],
            },
          },
          {
            className: "col-2",
            type: "select",
            key: "contract",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.contract"
              ),
              placeholder: this.translateService.instant(
                "settings.document.contract"
              ),
              required: true,
              multiple: false,
              options: [
                {
                  value: false,
                  label: "No",
                },
                {
                  value: true,
                  label: "Sì",
                },
              ],
            },
          },
          {
            className: "col-4",
            type: "select",
            key: "expirationNotice",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.expirationNotice"
              ),
              placeholder: this.translateService.instant(
                "settings.document.expirationNotice"
              ),
              required: false,
              multiple: false,
              options: [
                {
                  value: "",
                  label: "",
                },
                {
                  value: "7",
                  label: "7",
                },
                {
                  value: "30",
                  label: "30",
                },
                {
                  value: "60",
                  label: "60",
                },
                {
                  value: "90",
                  label: "90",
                },
                {
                  value: "120",
                  label: "120",
                },
                {
                  value: "150",
                  label: "150",
                },
                {
                  value: "180",
                  label: "180",
                },
                {
                  value: "F+30",
                  label: "F+30",
                },
              ],
            },
          },
          {
            className: "col-2",
            type: "select",
            key: "deleted",
            templateOptions: {
              label: this.translateService.instant("settings.document.deleted"),
              placeholder: this.translateService.instant(
                "settings.document.deleted"
              ),
              required: true,
              multiple: false,
              options: [
                {
                  value: false,
                  label: "No",
                },
                {
                  value: true,
                  label: "Sì",
                },
              ],
            },
          },
        ],
      },
    ];
    this.subformIsVisible = true;
  }

  /**
   * Handle save document template third level
   *
   * @since 1.0.0
   */
  onSaveSubDocumentTemplate(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "settings.document.dialog.saveMessage"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.dialogIsLoading = true;
        const dataToSend = {
          subContent: {
            deleted: this.data.item.deleted,
            translation: this.data.item.translation,
            elementType: this.data.item.elementType,
            structure: this.data.item.structure,
          },
          documents: this.documents.map((el: any) => {
            return {
              _id: el._id,
              translation: el.translation,
              deleted: el.deleted,
              mandatory: el.mandatory,
              contract: el.contract,
              key: el.key,
              schemaType: el.schemaType,
              expirationNotice:
                el.expirationNotice === null ? "" : el.expirationNotice,
            };
          }),
        };
        this.subscriptions.push(
          this.settingsService
            .updateDocument(
              this.data.documentTemplateId,
              this.data.areaId,
              this.data.subAreaId,
              dataToSend
            )
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              (res) => {
                // this.onGetDocument();
                this.toastrService.success(res.message);
                this.dialogIsLoading = false;
              },
              (err) => {
                // Choose one of the following error handling method.
                // 1) First one show a message right under the form fields
                //    (if the form is properly setted)
                // 2) Second one is used to show a mat-error under each field
                //    in the dynamic form (is meant to be used only in dynamicForm)
                // 3) Third one show toastr notifications for each error
                // 4) Fourth one is used to show mat-error under each field
                //    in the ngx-formly form (is mean to be used only with ngx-formly)
                // this.helperService.handleFormError(form, err);
                // this.helperService.handleDynamicFormError(form, err);
                this.helperService.handleError(err);
                this.dialogIsLoading = false;
              }
            )
        );
      });
  }

  /**
   * Handle add new element of third level
   *
   * @since 1.0.0
   */
  onAddSubDocumentTemplate(): void {
    this.subformIsVisible = false;

    this.newSubModel = [];
    this.newSubFields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            key: "_id",
            defaultValue: "",
            templateOptions: {
              type: "hidden",
            },
          },
          {
            key: "key",
            defaultValue: "",
            templateOptions: {
              type: "hidden",
            },
          },
          {
            key: "deleted",
            defaultValue: "",
            templateOptions: {
              type: "hidden",
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "input",
            key: "translation.it",
            templateOptions: {
              label: this.translateService.instant("settings.document.it"),
              placeholder: this.translateService.instant(
                "settings.document.it"
              ),
              required: true,
              minLength: 2,
              maxLength: 500,
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "input",
            key: "translation.en",
            templateOptions: {
              label: this.translateService.instant("settings.document.en"),
              placeholder: this.translateService.instant(
                "settings.document.en"
              ),
              required: true,
              minLength: 2,
              maxLength: 500,
            },
          },
        ],
      },
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-2",
            type: "select",
            key: "schemaType",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.schemaType"
              ),
              placeholder: this.translateService.instant(
                "settings.document.schemaType"
              ),
              required: true,
              multiple: false,
              options: [
                { value: "ASchema", label: "A" },
                { value: "A1Schema", label: "A1" },
                { value: "A3mSchema", label: "A3m" },
                { value: "A4PlusSchema", label: "A4+" },
                { value: "A4Schema", label: "A4" },
                { value: "A5Schema", label: "A5" },
                { value: "A5mSchema", label: "A5m" },
                { value: "A7Schema", label: "A7" },
                { value: "A8Schema", label: "A8" },
                { value: "A8PlusSchema", label: "A8+" },
                { value: "A9Schema", label: "A9" },
                { value: "B2Schema", label: "B2" },
                { value: "B3Schema", label: "B3" },
                { value: "B5Schema", label: "B5" },
                { value: "B6Schema", label: "B6" },
                { value: "B6StarSchema", label: "B6*" },
                { value: "BBMinusSchema", label: "BB-" },
                { value: "BBPlusSchema", label: "BB+" },
                { value: "BBSchema", label: "BB" },
                { value: "BBSlashSchema", label: "BB/" },
                { value: "BBStarSchema", label: "BB*" },
                { value: "BMinusSchema", label: "B-" },
                { value: "BPlusSchema", label: "B+" },
                { value: "BSchema", label: "B" },
                { value: "BSlashSchema", label: "B/" },
                { value: "BStarSchema", label: "B*" },
                { value: "BXSchema", label: "BX" },
                { value: "C1Schema", label: "C1" },
                { value: "D1Schema", label: "D1" },
                { value: "DSchema", label: "D" },
                { value: "E1Schema", label: "E1" },
                { value: "ESchema", label: "E" },
                { value: "F1Schema", label: "F1" },
                { value: "F2Schema", label: "F2" },
                { value: "FSchema", label: "F" },
                { value: "G1Schema", label: "G1" },
                { value: "GMinusSchema", label: "G-" },
                { value: "GPlusSchema", label: "G+" },
                { value: "GSchema", label: "G" },
                { value: "GSlashSchema", label: "G/" },
                { value: "GStarSchema", label: "G*" },
                { value: "H1Schema", label: "H1" },
                { value: "I1Schema", label: "I1" },
                { value: "ISchema", label: "I" },
                { value: "L2Schema", label: "L2" },
                { value: "LPlusSchema", label: "L+" },
                { value: "LSchema", label: "L" },
                { value: "M1Schema", label: "M1" },
                { value: "NSchema", label: "N" },
                { value: "PSchema", label: "P" },
                { value: "QSchema", label: "Q" },
                { value: "RSchema", label: "R" },
                { value: "SSchema", label: "S" },
                { value: "TSchema", label: "T" },
                { value: "TXSchema", label: "TX" },
                { value: "USchema", label: "U" },
                { value: "VSchema", label: "V" },
                { value: "WPlusSchema", label: "W+" },
                { value: "WSchema", label: "W" },
                { value: "XSchema", label: "X" },
                { value: "XX1MinusSchema", label: "XX1-" },
                { value: "XX1PlusSchema", label: "XX1+" },
                { value: "XX1Schema", label: "XX1" },
                { value: "XX1SlashSchema", label: "XX1/" },
                { value: "XX1StarSchema", label: "XX1*" },
                { value: "YSchema", label: "Y" },
                { value: "Z1Schema", label: "Z1" },
                { value: "Z1PlusSchema", label: "Z1+" },
                { value: "Z3Schema", label: "Z3" },
                { value: "Z4Schema", label: "Z4" },
                { value: "Zschema", label: "Zschema" },
              ],
            },
          },
          {
            className: "col-2",
            type: "select",
            key: "mandatory",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.mandatory"
              ),
              placeholder: this.translateService.instant(
                "settings.document.mandatory"
              ),
              required: true,
              multiple: false,
              options: [
                {
                  value: false,
                  label: "No",
                },
                {
                  value: true,
                  label: "Sì",
                },
              ],
            },
          },
          {
            className: "col-2",
            type: "select",
            key: "contract",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.contract"
              ),
              placeholder: this.translateService.instant(
                "settings.document.contract"
              ),
              required: true,
              multiple: false,
              options: [
                {
                  value: false,
                  label: "No",
                },
                {
                  value: true,
                  label: "Sì",
                },
              ],
            },
          },
          {
            className: "col-4",
            type: "select",
            key: "expirationNotice",
            templateOptions: {
              label: this.translateService.instant(
                "settings.document.expirationNotice"
              ),
              placeholder: this.translateService.instant(
                "settings.document.expirationNotice"
              ),
              required: false,
              multiple: false,
              options: [
                {
                  value: "",
                  label: "",
                },
                {
                  value: "7",
                  label: "7",
                },
                {
                  value: "30",
                  label: "30",
                },
                {
                  value: "60",
                  label: "60",
                },
                {
                  value: "90",
                  label: "90",
                },
                {
                  value: "120",
                  label: "120",
                },
                {
                  value: "150",
                  label: "150",
                },
                {
                  value: "180",
                  label: "180",
                },
                {
                  value: "F+30",
                  label: "F+30",
                },
              ],
            },
          },
          {
            className: "col-2",
            type: "select",
            key: "deleted",
            templateOptions: {
              label: this.translateService.instant("settings.document.deleted"),
              placeholder: this.translateService.instant(
                "settings.document.deleted"
              ),
              required: true,
              multiple: false,
              options: [
                {
                  value: false,
                  label: "No",
                },
                {
                  value: true,
                  label: "Sì",
                },
              ],
            },
          },
        ],
      },
    ];

    this.newSubformIsVisible = true;
  }

  /**
   * Handle dialog Submit
   *
   * @since 1.0.0
   */
  onSubFormSubmit(data?: any): void {
    if (this.subForm.invalid) {
      Object.keys(this.subForm.controls).forEach((key) => {
        this.subForm.controls[key].markAsTouched();
      });
      console.warn("form", this.subForm);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }
    // Activate submit button loading status
    this.formBtnOptions.active = true;

    if (this.subForm.value) {
      const dataToSend = {
        subContent: {
          deleted: this.data.item.deleted,
          translation: this.data.item.translation,
          elementType: this.data.item.elementType,
          structure: this.data.item.structure,
        },
        documents: this.documents.map((el: any) => {
          if (el._id.toString() === this.subForm.value._id.toString()) {
            return {
              _id: el._id,
              translation: this.subForm.value.translation,
              deleted: this.subForm.value.deleted,
              mandatory: this.subForm.value.mandatory,
              contract: this.subForm.value.contract,
              key: el.key,
              schemaType: this.subForm.value.schemaType,
              expirationNotice:
              this.subForm.value.expirationNotice === null ? "" : this.subForm.value.expirationNotice,
            };
          }
          return {
            _id: el._id,
            translation: el.translation,
            deleted: el.deleted,
            mandatory: el.mandatory,
            contract: el.contract,
            key: el.key,
            schemaType: el.schemaType,
            expirationNotice:
              el.expirationNotice === null ? "" : el.expirationNotice,
          };
        }),
      };

      this.dialogIsLoading = true;
      this.subscriptions.push(
        this.settingsService
          .updateDocument(
            this.data.documentTemplateId,
            this.data.areaId,
            this.data.subAreaId,
            dataToSend
          )
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            (res) => {
              this.dialogIsLoading = false;
              if (res.status) {
                this.subformIsVisible = false;
                this.toastrService.success(res.message);
                // this.dialogRef.close({
                //   save: "success"
                // });
              }
            },
            (err) => {
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }
  }

  /**
   * Handle dialog Submit
   *
   * @since 1.0.0
   */
  onNewSubFormSubmit(data?: any): void {
    if (this.newSubForm.invalid) {
      Object.keys(this.newSubForm.controls).forEach((key) => {
        this.newSubForm.controls[key].markAsTouched();
      });
      console.warn("form", this.newSubForm);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }
    // Activate submit button loading status
    this.formBtnOptions.active = true;

    if (this.newSubForm.value) {
      const dataToSend = {
        documents: this.newSubForm.value,
      };

      this.dialogIsLoading = true;
      this.subscriptions.push(
        this.settingsService
          .createDocument(
            this.data.documentTemplateId,
            this.data.areaId,
            this.data.subAreaId,
            dataToSend
          )
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            (res) => {
              this.dialogIsLoading = false;
              if (res.status) {
                this.newSubformIsVisible = false;
                this.documents = Array.isArray(res.thirdLevelDocs)
                  ? res.thirdLevelDocs
                  : [];
                this.toastrService.success(res.message);
              }
            },
            (err) => {
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }
  }

  /**
   * Handle move of the selected third-level document to another content (section).
   * Source content is the one currently opened in this dialog; destination is picked
   * from the grouped-by-area select.
   *
   * @since 3.0.0
   */
  onMoveDocumentSubmit(): void {
    if (this.moveDocForm.invalid) {
      Object.keys(this.moveDocForm.controls).forEach((key) => {
        this.moveDocForm.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "settings.document.move.confirmMessageDocument"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        const docId = this.moveDocForm.value.docId;
        const dest = (this.moveDocForm.value.dest || "").toString();
        const separatorIndex = dest.indexOf("::");
        if (separatorIndex < 0) {
          return;
        }
        const destAreaId = dest.substring(0, separatorIndex);
        const destContentId = dest.substring(separatorIndex + 2);

        this.dialogIsLoading = true;
        this.subscriptions.push(
          this.settingsService
            .moveDocumentDocument(
              this.data.documentTemplateId,
              this.data.areaId,
              this.data.subAreaId,
              destAreaId,
              destContentId,
              docId
            )
            .pipe(finalize(() => (this.dialogIsLoading = false)))
            .subscribe(
              (res) => {
                if (res && res.status) {
                  this.toastrService.success(res.message);
                  // Reflect the document leaving the current content
                  this.refreshDocumentsFromTemplate(res.data);
                  this.subformIsVisible = false;
                  this.moveDocForm.reset();
                }
              },
              (err) => {
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Refresh the third-level documents list of the current content from a freshly
   * returned (full) template, so the dialog reflects the move without reopening.
   *
   * @since 3.0.0
   */
  refreshDocumentsFromTemplate(template: any): void {
    if (!template || !Array.isArray(template.area)) {
      return;
    }
    const sourceArea = template.area.find(
      (area: any) =>
        area &&
        area._id &&
        area._id.toString() === this.data.areaId.toString()
    );
    if (!sourceArea || !Array.isArray(sourceArea.content)) {
      return;
    }
    const sourceContent = sourceArea.content.find(
      (content: any) =>
        content &&
        content._id &&
        content._id.toString() === this.data.subAreaId.toString()
    );
    if (sourceContent && Array.isArray(sourceContent.documents)) {
      this.documents = sourceContent.documents;
    }
  }
}
